import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../pages/app/protected/firebase';
import { collection, addDoc, getDocs, query, orderBy, where, Timestamp, runTransaction, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { FiPlus, FiSearch, FiCalendar, FiFilter, FiRefreshCw, FiFileText, FiDollarSign } from 'react-icons/fi';
import { format, startOfDay, endOfDay } from 'date-fns';

const Journal = () => {
  const navigate = useNavigate();
  const { user, companyId } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');
  const [filters, setFilters] = useState({
    startDate: today,
    endDate: today,
    reference: '',
  });
  const [newEntry, setNewEntry] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    reference: '',
    description: '',
    entries: [
      { account: '', debit: '', credit: '' },
      { account: '', debit: '', credit: '' },
    ],
  });
  const [accounts, setAccounts] = useState([
    { id: 'cash', name: 'Cash' },
    { id: 'accounts_receivable', name: 'Accounts Receivable' },
    { id: 'inventory', name: 'Inventory' },
    { id: 'accounts_payable', name: 'Accounts Payable' },
    { id: 'revenue', name: 'Revenue' },
    { id: 'expenses', name: 'Expenses' },
    { id: 'equity', name: 'Equity' },
  ]);

  // Fetch journal entries
  const fetchJournalEntries = async () => {
    if (!user?.uid || !companyId) return;
    
    setLoading(true);
    try {
      // Convert filter dates to Date objects at start and end of day
      const startDate = startOfDay(new Date(filters.startDate));
      const endDate = endOfDay(new Date(filters.endDate));
      
      // Create base query with date range filter
      let q = query(
        collection(db, 'companies', companyId, 'journalEntries'),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate)),
        orderBy('date', 'desc')
      );
      
      // Add reference filter if provided
      if (filters.reference) {
        q = query(q, where('reference', '>=', filters.reference));
        q = query(q, where('reference', '<=', filters.reference + '\uf8ff'));
      }
      
      const querySnapshot = await getDocs(q);
      const entriesData = [];
      
      querySnapshot.forEach((doc) => {
        entriesData.push({ id: doc.id, ...doc.data() });
      });
      
      setEntries(entriesData);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch journal entries when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchJournalEntries();
    }, 300); // Add a small debounce to prevent too many queries
    
    return () => clearTimeout(timer);
  }, [filters]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEntry(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle entry line changes
  const handleEntryChange = (index, field, value) => {
    const updatedEntries = [...newEntry.entries];
    updatedEntries[index] = { ...updatedEntries[index], [field]: value };
    
    // Auto-calculate the other amount if needed
    if (field === 'debit' && value) {
      updatedEntries[index].credit = '';
    } else if (field === 'credit' && value) {
      updatedEntries[index].debit = '';
    }
    
    setNewEntry(prev => ({
      ...prev,
      entries: updatedEntries
    }));
  };

  // Add a new entry line
  const addEntryLine = () => {
    setNewEntry(prev => ({
      ...prev,
      entries: [...prev.entries, { account: '', debit: '', credit: '' }]
    }));
  };

  // Remove an entry line
  const removeEntryLine = (index) => {
    if (newEntry.entries.length <= 2) return; // Need at least 2 lines for double-entry
    
    setNewEntry(prev => ({
      ...prev,
      entries: prev.entries.filter((_, i) => i !== index)
    }));
  };

  // Save journal entry
  const saveJournalEntry = async () => {
    setIsSaving(true);
    // Check if reference exists (only for new entries)
    if (!isEditMode) {
      const refQuery = query(
        collection(db, 'companies', companyId, 'journalEntries'),
        where('reference', '==', newEntry.reference.trim())
      );
      
      const refSnapshot = await getDocs(refQuery);
      if (!refSnapshot.empty) {
        alert('A journal entry with this reference number already exists');
        setIsSaving(false);
        return;
      }
    }
    
    // Validate entries
    const totalDebit = newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0);
    const totalCredit = newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0);
    
    if (Math.abs(totalDebit - totalCredit) > 0.01) { // Allow for floating point imprecision
      alert('Total debits must equal total credits');
      setIsSaving(false);
      return;
    }
    
    if (newEntry.entries.some(entry => !entry.account)) {
      alert('All entries must have an account selected');
      setIsSaving(false);
      return;
    }
    
    if (!newEntry.reference) {
      alert('Reference is required');
      setIsSaving(false);
      return;
    }
    
    try {
      const entryData = {
        ...newEntry,
        date: Timestamp.fromDate(new Date(newEntry.date)),
        updatedAt: Timestamp.now(),
        entries: newEntry.entries.map(entry => ({
          ...entry,
          debit: parseFloat(entry.debit) || 0,
          credit: parseFloat(entry.credit) || 0,
        }))
      };
      
      if (isEditMode && editingEntryId) {
        // Update existing entry
        await updateDoc(doc(db, 'companies', companyId, 'journalEntries', editingEntryId), entryData);
      } else {
        // Create new entry
        entryData.createdAt = Timestamp.now();
        entryData.createdBy = user.uid;
        await addDoc(collection(db, 'companies', companyId, 'journalEntries'), entryData);
      }
      
      // Update account balances
      await updateAccountBalances(entryData.entries);
      
      // Reset form and refresh data
      resetForm();
      fetchJournalEntries();
      
    } catch (error) {
      console.error('Error saving journal entry:', error);
      alert('Failed to save journal entry');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Update account balances after journal entry
  const updateAccountBalances = async (entries) => {
    const batch = [];
    
    for (const entry of entries) {
      const accountRef = doc(db, 'companies', companyId, 'accounts', entry.account);
      
      // In a real app, you would update the account balance here
      // This is a simplified version
      console.log(`Updating account ${entry.account} with debit: ${entry.debit}, credit: ${entry.credit}`);
    }
    
    // In a real app, you would commit the batch here
    // await batch.commit();
  };

  // Apply filters
  const applyFilters = async () => {
    if (!user?.uid || !companyId) return;
    
    setLoading(true);
    try {
      let q = query(
        collection(db, 'companies', companyId, 'journalEntries'),
        orderBy('date', 'desc')
      );
      
      // Apply date filters if set
      if (filters.startDate || filters.endDate) {
        const start = filters.startDate ? startOfDay(new Date(filters.startDate)) : null;
        const end = filters.endDate ? endOfDay(new Date(filters.endDate)) : null;
        
        if (start && end) {
          q = query(q, where('date', '>=', Timestamp.fromDate(start)), where('date', '<=', Timestamp.fromDate(end)));
        } else if (start) {
          q = query(q, where('date', '>=', Timestamp.fromDate(start)));
        } else if (end) {
          q = query(q, where('date', '<=', Timestamp.fromDate(end)));
        }
      }
      
      const querySnapshot = await getDocs(q);
      let entriesData = [];
      
      querySnapshot.forEach((doc) => {
        entriesData.push({ id: doc.id, ...doc.data() });
      });
      
      // Apply reference filter client-side if provided
      if (filters.reference) {
        const refLower = filters.reference.toLowerCase();
        entriesData = entriesData.filter(entry => 
          entry.reference && entry.reference.toLowerCase().includes(refLower)
        );
      }
      
      setEntries(entriesData);
    } catch (error) {
      console.error('Error fetching filtered journal entries:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      startDate: today,
      endDate: today,
      reference: '',
    });
    fetchJournalEntries();
  };

  // View journal entry
  const handleViewEntry = (entry) => {
    const formattedEntry = {
      ...entry,
      date: entry.date?.toDate ? format(entry.date.toDate(), 'yyyy-MM-dd') : '',
      entries: entry.entries?.map(e => ({ ...e })) || []
    };
    
    setNewEntry(formattedEntry);
    setIsEditMode(true);
    setEditingEntryId(entry.id);
    setShowNewEntry(true);
  };

  // Delete journal entry
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this journal entry? This action cannot be undone.')) {
      return;
    }
    
    if (!user?.uid || !companyId) return;
    
    try {
      // In a real app, you might want to update account balances here before deleting
      // await updateAccountBalancesBeforeDelete(entryId);
      
      await deleteDoc(doc(db, 'companies', companyId, 'journalEntries', entryId));
      
      // Refresh the entries list
      fetchJournalEntries();
      
      // Show success message
      alert('Journal entry deleted successfully');
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      alert('Failed to delete journal entry');
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchJournalEntries();
  }, [user]);

  // Reset form
  const resetForm = () => {
    setNewEntry({
      date: format(new Date(), 'yyyy-MM-dd'),
      reference: '',
      description: '',
      entries: [
        { account: '', debit: '', credit: '' },
        { account: '', debit: '', credit: '' },
      ],
    });
    setIsEditMode(false);
    setEditingEntryId(null);
    setShowNewEntry(false);
  };

  // Handle date change with auto-filtering
  const handleDateChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      
      // Auto-adjust the other date if needed to maintain valid range
      if (field === 'startDate' && new Date(value) > new Date(prev.endDate)) {
        newFilters.endDate = value;
      } else if (field === 'endDate' && new Date(value) < new Date(prev.startDate)) {
        newFilters.startDate = value;
      }
      
      return newFilters;
    });
  };

  return (
    <div className="text-white md:px-6">
      <div className="flex items-center mb-4 gap-2">
        <button 
          onClick={() => navigate('/accounting')} 
          className="text-sm flex items-center gap-1 text-gray-300 hover:text-white"
        >
          <span className="text-lg">←</span> Back
        </button>
      </div>
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Journal Entries</h1>
        <button
          onClick={() => setShowNewEntry(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
        >
          <FiPlus /> New Entry
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-300">Start Date</label>
              <input
                type="date"
                max={filters.endDate}
                value={filters.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                className="w-1/2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-300">End Date</label>
              <input
                type="date"
                min={filters.startDate}
                value={filters.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                className="w-1/2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by reference..."
                value={filters.reference || ''}
                onChange={(e) => setFilters({ ...filters, reference: e.target.value })}
                className="w-full px-3 py-2 pl-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <FiSearch className="absolute left-3 top-2.5 text-gray-400 pointer-events-none" />
            </div>
            
            <div className="flex space-x-2">
            <button
                onClick={applyFilters}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <FiSearch size={16} /> Search
              </button>
              <button
                onClick={resetFilters}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center justify-center gap-2"
              >
                <FiRefreshCw size={16} /> Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Journal Entries Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Reference
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Debit (+)
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Credit (-)
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-400">
                    No journal entries found
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <React.Fragment key={entry.id}>
                    <tr className="bg-gray-800 hover:bg-gray-750">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {entry.date?.toDate ? format(entry.date.toDate(), 'MMM dd, yyyy') : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 font-medium">
                        {entry.reference || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300 max-w-xs truncate">
                        {entry.description || 'No description'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-medium ${
                        entry.entries?.reduce((sum, e) => sum + (e.debit || 0), 0) !== 0 ? 'text-green-500' : 'text-gray-300'
                      }`}>
                        {entry.entries?.reduce((sum, e) => sum + (e.debit || 0), 0).toFixed(2)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-medium ${
                        entry.entries?.reduce((sum, e) => sum + (e.credit || 0), 0) !== 0 ? 'text-red-500' : 'text-gray-300'
                      }`}>
                        {entry.entries?.reduce((sum, e) => sum + (e.credit || 0), 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          onClick={() => handleViewEntry(entry)}
                          className="text-purple-500 hover:text-purple-400 mr-3"
                          title="Edit Entry"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button 
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="text-red-500 hover:text-red-400"
                          title="Delete Entry"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                    {entry.entries?.map((line, idx) => (
                      <tr key={`${entry.id}-${idx}`} className="bg-gray-750 text-xs">
                        <td colSpan="2" className="px-6 py-1 text-gray-400">
                          {accounts.find(a => a.id === line.account)?.name || line.account}
                        </td>
                        <td className="px-6 py-1 text-gray-400">
                          {line.description || 'No description'}
                        </td>
                        <td className={`px-6 py-1 text-right ${
                          line.debit !== 0 ? 'text-green-500' : 'text-gray-300'
                        }`}>
                          {line.debit ? line.debit.toFixed(2) : '0.00'}
                        </td>
                        <td className={`px-6 py-1 text-right ${
                          line.credit !== 0 ? 'text-red-500' : 'text-gray-300'
                        }`}>
                          {line.credit ? line.credit.toFixed(2) : '0.00'}
                        </td>
                        <td></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Journal Entry Modal */}
      {showNewEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">New Journal Entry</h2>
                <button 
                  onClick={resetForm}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={newEntry.date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Reference *</label>
                  <input
                    type="text"
                    name="reference"
                    value={newEntry.reference}
                    onChange={handleInputChange}
                    placeholder="e.g. JRNL-001"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    readOnly={isEditMode}
                    style={isEditMode ? { backgroundColor: '#374151', cursor: 'not-allowed' } : {}}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                  <input
                    type="text"
                    name="description"
                    value={newEntry.description}
                    onChange={handleInputChange}
                    placeholder="Enter a description for this journal entry"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium">Entries</h3>
                  <button
                    type="button"
                    onClick={addEntryLine}
                    className="text-sm text-blue-500 hover:text-blue-400 flex items-center gap-1"
                  >
                    <FiPlus size={16} /> Add Line
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead>
                      <tr className="text-xs text-gray-400 text-left">
                        <th className="px-3 py-2">Account *</th>
                        <th className="px-3 py-2 text-right">Debit (+)</th>
                        <th className="px-3 py-2 text-right">Credit (-)</th>
                        <th className="px-3 py-2">Description</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {newEntry.entries.map((entry, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2">
                            <select
                              name="account"
                              value={entry.account}
                              onChange={(e) => handleEntryChange(index, 'account', e.target.value)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              required
                            >
                              <option value="">Select Account</option>
                              {accounts.map(account => (
                                <option key={account.id} value={account.id}>
                                  {account.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              name="debit"
                              value={entry.debit}
                              onChange={(e) => handleEntryChange(index, 'debit', e.target.value)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-green-500 text-right text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="0.00"
                              disabled={!!entry.credit}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              name="credit"
                              value={entry.credit}
                              onChange={(e) => handleEntryChange(index, 'credit', e.target.value)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-red-500 text-right text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="0.00"
                              disabled={!!entry.debit}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              name="lineDescription"
                              value={entry.description || ''}
                              onChange={(e) => handleEntryChange(index, 'description', e.target.value)}
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Description (optional)"
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            {newEntry.entries.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeEntryLine(index)}
                                className="text-red-500 hover:text-red-400"
                                title="Remove line"
                              >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-gray-700">
                        <td className="px-3 py-2 text-sm font-medium">Total</td>
                        <td className={`px-3 py-2 text-right text-sm font-medium ${
                          newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0) !== 0 ? 'text-green-500' : 'text-gray-300'
                        }`}>
                          {newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0).toFixed(2)}
                        </td>
                        <td className={`px-3 py-2 text-right text-sm font-medium ${
                          newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0) !== 0 ? 'text-red-500' : 'text-gray-300'
                        }`}>
                          {newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0).toFixed(2)}
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                      <tr className="border-t border-gray-700">
                        <td colSpan="5" className="px-3 py-2 text-right">
                          <span className={`text-sm ${
                            Math.abs(
                              newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0) -
                              newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0)
                            ) > 0.01 ? 'text-red-500' : 'text-green-500'
                          }`}>
                            {Math.abs(
                              newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0) -
                              newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0)
                            ) > 0.01 ? 'Out of Balance' : 'In Balance'}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveJournalEntry}
                  disabled={
                    isSaving ||
                    Math.abs(
                      newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0) -
                      newEntry.entries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0)
                    ) > 0.01 ||
                    !newEntry.date ||
                    newEntry.entries.some(entry => !entry.account) ||
                    newEntry.entries.every(entry => !entry.debit && !entry.credit) ||
                    !newEntry.reference
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[150px]"
                >
                  {isSaving ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : isEditMode ? (
                    'Update Entry'
                  ) : (
                    'Save Journal Entry'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Journal;